// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Check,
  Upload,
  Sparkles,
  Palette,
  Image as ImageIcon,
  Grid,
  Layers,
  Eye,
  Info,
  Trash2,
} from 'lucide-react';
import { FloorMaterialOption } from '../types';
import {
  resolveColorToHex,
  adjustColorBrightness,
  getCheckerboardColorTones,
} from '../utils/floorColorUtils';
import '../styles/twin-drawers-shared.css';
import '../styles/twin-icons-shared.css';
import './FloorMaterialDrawer.css';
import AppIcon from './AppIcon';

interface FloorMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (material: FloorMaterialOption) => void;
  onDelete?: (material: FloorMaterialOption) => void;
  initialMaterial?: FloorMaterialOption | null;
}

const PRESET_COLOR_PALETTES = [
  { label: 'Tech Cyan', hex: '#0891b2' },
  { label: 'Emerald Green', hex: '#059669' },
  { label: 'Oak / Amber Wood', hex: '#d97706' },
  { label: 'Mahogany Wood', hex: '#92400e' },
  { label: 'Rustic Terracotta', hex: '#c2410c' },
  { label: 'Sky / Royal Blue', hex: '#0284c7' },
  { label: 'Neon Purple', hex: '#9333ea' },
  { label: 'Graphite Gray', hex: '#475569' },
  { label: 'Light Marble', hex: '#e2e8f0' },
  { label: 'Obsidian / Server', hex: '#1e1b4b' },
];

const PRESET_EMOJIS = ['🪵', '🔷', '🏢', '🟧', '🚽', '☕', '🏛️', '⚡', '🟩', '🟫', '⬛', '⬜', '🪨', '🧱', '🎨', '✨'];

// === COMPONENTE: DRAWER DE CONFIGURACIÓN DE TEXTURAS DE PISO ===
export const FloorMaterialDrawer: React.FC<FloorMaterialModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialMaterial,
}) => {
  const isEditing = Boolean(initialMaterial);
  const drawerRef = useRef<HTMLElement>(null);

  // === CIERRE POR CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) return;
      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // === ESTADO DEL FORMULARIO ===
  const [name, setName] = useState<string>('');
  const [icon, setIcon] = useState<string>('🎨');
  const [materialType, setMaterialType] = useState<'color' | 'image'>('color');

  const [primaryColor, setPrimaryColor] = useState<string>('#0891b2');
  const [customSecondaryColor, setCustomSecondaryColor] = useState<string>('#0e7490');
  const [useCustomSecondaryColor, setUseCustomSecondaryColor] = useState<boolean>(false);

  const [hasCheckerboard, setHasCheckerboard] = useState<boolean>(true);
  const [imageCheckerMode, setImageCheckerMode] = useState<'auto_filter' | 'dual_image'>('auto_filter');
  const [checkerBrightnessDelta, setCheckerBrightnessDelta] = useState<number>(-22);

  const [imagePrimary, setImagePrimary] = useState<string | undefined>(undefined);
  const [imageSecondary, setImageSecondary] = useState<string | undefined>(undefined);

  const fileInputPrimaryRef = useRef<HTMLInputElement>(null);
  const fileInputSecondaryRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // === POBLAR ESTADO AL ABRIR / CAMBIAR MATERIAL INICIAL ===
  useEffect(() => {
    if (isOpen) {
      if (initialMaterial) {
        setName(initialMaterial.name);
        setIcon(initialMaterial.icon || '🎨');
        const hasImg = Boolean(initialMaterial.customImage);
        setMaterialType(hasImg ? 'image' : 'color');
        setPrimaryColor(resolveColorToHex(initialMaterial.previewColor));
        setCustomSecondaryColor(
          initialMaterial.secondaryColor
            ? resolveColorToHex(initialMaterial.secondaryColor)
            : adjustColorBrightness(resolveColorToHex(initialMaterial.previewColor), -18)
        );
        setUseCustomSecondaryColor(Boolean(initialMaterial.secondaryColor));
        setHasCheckerboard(initialMaterial.hasCheckerboard !== false);
        setImagePrimary(initialMaterial.customImage);
        setImageSecondary(initialMaterial.customImageSecondary);
        setImageCheckerMode(initialMaterial.customImageSecondary ? 'dual_image' : (initialMaterial.checkerMode === 'dual_image' ? 'dual_image' : 'auto_filter'));
        if (initialMaterial.checkerBrightness !== undefined) {
          setCheckerBrightnessDelta(Math.round((initialMaterial.checkerBrightness - 1) * 100));
        } else {
          setCheckerBrightnessDelta(-22);
        }
      } else {
        setName('');
        setIcon('🎨');
        setMaterialType('color');
        setPrimaryColor('#0891b2');
        setCustomSecondaryColor('#0e7490');
        setUseCustomSecondaryColor(false);
        setHasCheckerboard(true);
        setImageCheckerMode('auto_filter');
        setCheckerBrightnessDelta(-22);
        setImagePrimary(undefined);
        setImageSecondary(undefined);
      }
    }
  }, [isOpen, initialMaterial]);

  // === MANEJADOR DE SUBIDA DE ARCHIVOS ===
  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (uri: string | undefined) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('The image must not exceed 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) setter(result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // === RENDERIZADO DE VISTA PREVIA EN VIVO (CANVAS) ===
  // Nota: esta lógica es JavaScript puro de Canvas 2D, sin dependencia de Tailwind.
  useEffect(() => {
    if (!isOpen) return;
    const canvas = previewCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = 280;
    const height = 160;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, width, height);

    const tileW = 56;
    const tileH = 28;
    const originX = width / 2;
    const originY = 32;

    const loadedImgA = imagePrimary ? new Image() : null;
    if (loadedImgA && imagePrimary) loadedImgA.src = imagePrimary;

    const loadedImgB = imageSecondary ? new Image() : null;
    if (loadedImgB && imageSecondary) loadedImgB.src = imageSecondary;

    const drawGrid = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#090d16';
      ctx.fillRect(0, 0, width, height);

      for (let c = 0; c < 3; c++) {
        for (let r = 0; r < 3; r++) {
          const x = originX + (c - r) * (tileW / 2);
          const y = originY + (c + r) * (tileH / 2);
          const isEven = (c + r) % 2 === 0;

          const hw = tileW / 2;
          const hh = tileH / 2;

          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + hw, y + hh);
          ctx.lineTo(x, y + tileH);
          ctx.lineTo(x - hw, y + hh);
          ctx.closePath();

          if (materialType === 'image' && imagePrimary) {
            ctx.save();
            ctx.clip();

            if (hasCheckerboard) {
              if (imageCheckerMode === 'dual_image' && imageSecondary && loadedImgB && loadedImgB.complete) {
                const imgToDraw = isEven ? loadedImgA : loadedImgB;
                if (imgToDraw && imgToDraw.complete) {
                  ctx.drawImage(imgToDraw, x - hw, y, tileW, tileH);
                }
              } else if (loadedImgA && loadedImgA.complete) {
                ctx.drawImage(loadedImgA, x - hw, y, tileW, tileH);
                if (!isEven) {
                  const alpha = Math.min(0.6, Math.abs(checkerBrightnessDelta) / 100);
                  const isDarken = checkerBrightnessDelta <= 0;
                  ctx.fillStyle = isDarken ? `rgba(0, 0, 0, ${alpha})` : `rgba(255, 255, 255, ${alpha})`;
                  ctx.fill();
                }
              }
            } else {
              if (loadedImgA && loadedImgA.complete) {
                ctx.drawImage(loadedImgA, x - hw, y, tileW, tileH);
              }
            }
            ctx.restore();

            ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
            ctx.lineWidth = 1;
            ctx.stroke();
          } else {
            const tones = getCheckerboardColorTones(
              primaryColor,
              useCustomSecondaryColor ? customSecondaryColor : undefined,
              hasCheckerboard,
              checkerBrightnessDelta
            );

            ctx.fillStyle = isEven ? tones.primary : tones.secondary;
            ctx.fill();

            ctx.strokeStyle = isEven ? tones.highlight : tones.shadow;
            ctx.lineWidth = 1;
            ctx.stroke();

            if (isEven) {
              ctx.fillStyle = tones.highlight;
              ctx.fillRect(Math.round(x - 1), Math.round(y + 2), 2, 1);
            }
          }
        }
      }
    };

    drawGrid();
    if (loadedImgA) loadedImgA.onload = drawGrid;
    if (loadedImgB) loadedImgB.onload = drawGrid;
  }, [
    isOpen, materialType, primaryColor, customSecondaryColor, useCustomSecondaryColor,
    hasCheckerboard, imagePrimary, imageSecondary, imageCheckerMode, checkerBrightnessDelta,
  ]);

  if (!isOpen) return null;

  // === GUARDAR MATERIAL ===
  const handleSave = () => {
    if (!name.trim()) return;

    const baseColorHex = resolveColorToHex(primaryColor);
    const secColorHex = useCustomSecondaryColor
      ? resolveColorToHex(customSecondaryColor)
      : adjustColorBrightness(baseColorHex, checkerBrightnessDelta);

    const savedMat: FloorMaterialOption = {
      id: initialMaterial?.id || `custom_floor_${Date.now()}`,
      name: name.trim(),
      icon: icon.trim() || '🎨',
      previewColor: baseColorHex,
      secondaryColor: hasCheckerboard ? secColorHex : undefined,
      customImage: materialType === 'image' ? imagePrimary : undefined,
      customImageSecondary: materialType === 'image' && hasCheckerboard && imageCheckerMode === 'dual_image' ? imageSecondary : undefined,
      hasCheckerboard,
      checkerMode: materialType === 'image' ? imageCheckerMode : 'color_tone',
      checkerBrightness: hasCheckerboard ? 1 + (checkerBrightnessDelta / 100) : 1.0,
      isCustom: true,
      description: initialMaterial?.description || (materialType === 'image' ? 'Graphic texture' : 'Solid color material'),
    };

    onSave(savedMat);
    onClose();
  };

  // === RENDER ===
  return (
    <aside
      ref={drawerRef}
      id="floor-material-drawer"
      aria-label="Side Drawer: Configure Floor Texture"
      className={`twin-drawer twin-drawer--lg ${isOpen ? 'twin-drawer--open' : 'twin-drawer--closed'}`}
    >
      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-drawer-header-left">
          <div className="twin-drawer-icon-badge twin-floor-icon-badge"> <AppIcon icon={icon} name={name} className="twin-icon-sm" /></div>
          <div className="twin-drawer-title twin-floor-title">
            <span>{isEditing ? 'Configure Floor Texture' : 'Create Floor Texture'}</span>
            {hasCheckerboard && <span className="twin-floor-checker-tag">Checkerboard</span>}
          </div>
        </div>
        <button type="button" onClick={onClose} className="twin-drawer-close-btn" title="Close drawer (Esc)">
          <X className="twin-icon-sm" />
        </button>
      </div>

      {/* === CUERPO === */}
      <div className="twin-drawer-body">

        {/* === IDENTIDAD DEL MATERIAL === */}
        <div className="twin-drawer-card">
          <div className="twin-section-header">
            <Palette className="twin-icon-xs" />
            <h3 className="twin-section-heading twin-no-margin">Material Identity</h3>
          </div>

          <div className="twin-grid-3">
            <div className="twin-col-span-2">
              <label className="twin-field-label">Material / Floor Name</label>
              <input
                type="text"
                placeholder="e.g. Natural Oak, Royal Marble, Microcement..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="twin-input twin-input--floor"
              />
            </div>

            <div>
              <label className="twin-field-label">Icon / Emoji</label>
              <div className="twin-floor-emoji-row">
                <input
                  type="text"
                  placeholder="🎨"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  className="twin-input twin-input--floor twin-input--emoji"
                />
                <div className="twin-floor-emoji-presets">
                  {PRESET_EMOJIS.slice(0, 5).map((em) => (
                    <button key={em} type="button" onClick={() => setIcon(em)} className="twin-floor-emoji-btn">
                      {em}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === TIPO BASE === */}
        <div className="twin-drawer-card">
          <div className="twin-section-header">
            <Palette className="twin-icon-xs" />
            <h3 className="twin-section-heading twin-no-margin">Base Type</h3>
          </div>

          <div className="twin-floor-radio-group">
            <label className="twin-floor-radio-label">
              <input
                type="radio"
                name="materialType"
                checked={materialType === 'color'}
                onChange={() => setMaterialType('color')}
              />
              Solid Color & Palette
            </label>
            <label className="twin-floor-radio-label">
              <input
                type="radio"
                name="materialType"
                checked={materialType === 'image'}
                onChange={() => setMaterialType('image')}
              />
              Image Texture (PNG / JPG)
            </label>
          </div>
        </div>

        {/* === VISTA PREVIA ISOMÉTRICA EN VIVO === */}
        <div className="twin-drawer-card">
          <div className="twin-section-header">
            <Eye className="twin-icon-xs" />
            <h3 className="twin-section-heading twin-no-margin">Real-Time Isometric Preview</h3>
          </div>
          <span className="twin-floor-preview-mode-label">
            {hasCheckerboard ? 'Dual Tone Alternation (Checkerboard)' : 'Uniform / Smooth'}
          </span>
          <div className="twin-floor-canvas-wrap">
            <canvas ref={previewCanvasRef} style={{ width: '280px', height: '160px' }} className="twin-floor-canvas" />
          </div>
        </div>



        {/* === CONFIGURACIÓN DEL EFECTO CHECKERBOARD === */}
        <div className="twin-drawer-card twin-floor-checker-panel">
          <div className="twin-row-between twin-floor-toggle-row">
            <div className="twin-floor-toggle-text">
              <span className="twin-section-header twin-no-margin">
                <Grid className="twin-icon-xs twin-floor-accent-icon" />
                <h3 className="twin-section-heading twin-no-margin">Checkerboard Effect (Isometric Alternation)</h3>
              </span>
              <p className="twin-floor-checker-desc">
                Alternates color intensities or images on neighboring tiles to create three-dimensional depth.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setHasCheckerboard((prev) => !prev)}
              className={`twin-toggle-switch ${hasCheckerboard ? 'twin-toggle-switch--on' : ''}`}
              role="switch"
              aria-checked={hasCheckerboard}
            >
              <span className="twin-toggle-knob" />
            </button>
          </div>

          {hasCheckerboard && (
            <div className="twin-floor-checker-options">
              {materialType === 'color' && (
                <div className="twin-section-gap">
                  <div className="twin-row-between">
                    <span className="twin-field-label twin-no-margin">Automatic Checkerboard Contrast Intensity</span>
                    <span className="twin-floor-slider-value">{checkerBrightnessDelta}% brightness</span>
                  </div>

                  <input
                    type="range"
                    min={-40}
                    max={40}
                    step={2}
                    value={checkerBrightnessDelta}
                    onChange={(e) => setCheckerBrightnessDelta(parseInt(e.target.value, 10))}
                    className="twin-range twin-range--floor"
                  />
                  <div className="twin-row-between twin-floor-range-labels">
                    <span>Darker (-40%)</span>
                    <span>Subtle (-10%)</span>
                    <span>Brighter (+40%)</span>
                  </div>

                  <div className="twin-floor-custom-color-toggle">
                    <label className="twin-checkbox-inline">
                      <input
                        type="checkbox"
                        checked={useCustomSecondaryColor}
                        onChange={(e) => setUseCustomSecondaryColor(e.target.checked)}
                      />
                      <span>Choose a Manual Custom Secondary Color</span>
                    </label>

                    {useCustomSecondaryColor && (
                      <div className="twin-floor-secondary-color-row">
                        <input
                          type="color"
                          value={customSecondaryColor}
                          onChange={(e) => setCustomSecondaryColor(e.target.value)}
                          className="twin-floor-color-native-lg"
                        />
                        <span className="twin-floor-secondary-label">Odd tiles color:</span>
                        <span className="twin-floor-secondary-hex">{customSecondaryColor}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {materialType === 'image' && (
                <div className="twin-section-gap">
                  <span className="twin-field-label twin-no-margin">Alternation Method for Image Texture</span>

                  <div className="twin-grid-2">
                    <button
                      type="button"
                      onClick={() => setImageCheckerMode('auto_filter')}
                      className={`twin-floor-mode-card ${imageCheckerMode === 'auto_filter' ? 'twin-floor-mode-card--selected' : ''}`}
                    >
                      <span className="twin-floor-mode-card-title">
                        <Sparkles className="twin-icon-sm twin-floor-mode-icon-amber" />
                        <span>Smart Auto-Filter</span>
                      </span>
                      <span className="twin-floor-mode-card-desc">
                        Single Image: Applies an automatic contrast filter on alternating tiles.
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setImageCheckerMode('dual_image')}
                      className={`twin-floor-mode-card ${imageCheckerMode === 'dual_image' ? 'twin-floor-mode-card--selected' : ''}`}
                    >
                      <span className="twin-floor-mode-card-title">
                        <Layers className="twin-icon-sm twin-floor-accent-icon" />
                        <span>Upload 2 Images</span>
                      </span>
                      <span className="twin-floor-mode-card-desc">
                        Dual Image: Combines two distinct textures (Image A & Image B).
                      </span>
                    </button>
                  </div>

                  {imageCheckerMode === 'auto_filter' && (
                    <div className="twin-floor-filter-slider-box">
                      <div className="twin-row-between">
                        <span className="twin-floor-filter-label">Shading Filter Intensity</span>
                        <span className="twin-floor-slider-value">{checkerBrightnessDelta}%</span>
                      </div>
                      <input
                        type="range"
                        min={-45}
                        max={35}
                        step={5}
                        value={checkerBrightnessDelta}
                        onChange={(e) => setCheckerBrightnessDelta(parseInt(e.target.value, 10))}
                        className="twin-range twin-range--floor"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* === CONTENIDO ESPECÍFICO: PALETA DE COLOR O SUBIDA DE IMÁGENES === */}
        {materialType === 'color' ? (
          <div className="twin-drawer-card">
            <label className="twin-field-label">Primary Color (Main / Even Tiles)</label>

            <div className="twin-floor-palette-grid">
              {PRESET_COLOR_PALETTES.map((pal) => (
                <button
                  key={pal.hex}
                  type="button"
                  onClick={() => {
                    setPrimaryColor(pal.hex);
                    setCustomSecondaryColor(adjustColorBrightness(pal.hex, checkerBrightnessDelta));
                  }}
                  className={`twin-floor-palette-btn ${primaryColor.toLowerCase() === pal.hex.toLowerCase() ? 'twin-floor-palette-btn--selected' : ''}`}
                  title={pal.label}
                >
                  <div className="twin-floor-palette-dot" style={{ backgroundColor: pal.hex }} />
                  <span className="twin-floor-palette-label">{pal.label.split(' ')[0]}</span>
                </button>
              ))}
            </div>

            <div className="twin-floor-hex-row">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => {
                  setPrimaryColor(e.target.value);
                  setCustomSecondaryColor(adjustColorBrightness(e.target.value, checkerBrightnessDelta));
                }}
                className="twin-floor-color-native-lg"
              />
              <div className="twin-floor-hex-field">
                <span className="twin-floor-hex-hint">Hex Code:</span>
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="twin-input twin-input--floor twin-floor-hex-input"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="twin-drawer-card">
            {/* === IMAGEN A === */}
            <div className="twin-section-gap">
              <div className="twin-row-between">
                <label className="twin-field-label twin-no-margin">
                  Primary Image A {hasCheckerboard ? '(Even Tiles)' : '(Single Texture)'}
                </label>
                {imagePrimary && <span className="twin-floor-loaded-tag"><Check className="twin-icon-xs" /> Loaded</span>}
              </div>

              <div className="twin-floor-upload-row">
                <div className="twin-floor-upload-thumb">
                  {imagePrimary ? (
                    <img src={imagePrimary} alt="Image A" className="twin-floor-upload-img" />
                  ) : (
                    <ImageIcon className="twin-icon-md twin-floor-upload-placeholder-icon" />
                  )}
                </div>
                <div className="twin-floor-upload-actions">
                  <input type="file" ref={fileInputPrimaryRef} onChange={(e) => handleFileUpload(e, setImagePrimary)} accept="image/*" className="twin-hidden" />
                  <button type="button" onClick={() => fileInputPrimaryRef.current?.click()} className="twin-btn twin-btn--floor-primary twin-btn--flex">
                    <Upload className="twin-icon-sm" /> Upload Image A
                  </button>
                  {imagePrimary && (
                    <button type="button" onClick={() => setImagePrimary(undefined)} className="twin-floor-remove-btn" title="Delete image">
                      <X className="twin-icon-sm" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* === IMAGEN B (solo modo dual) === */}
            {hasCheckerboard && imageCheckerMode === 'dual_image' && (
              <div className="twin-section-gap twin-floor-image-b-section">
                <div className="twin-row-between">
                  <label className="twin-field-label twin-no-margin">Secondary Image B (Odd / Alternating Tiles)</label>
                  {imageSecondary && <span className="twin-floor-loaded-tag"><Check className="twin-icon-xs" /> Loaded</span>}
                </div>

                <div className="twin-floor-upload-row">
                  <div className="twin-floor-upload-thumb">
                    {imageSecondary ? (
                      <img src={imageSecondary} alt="Image B" className="twin-floor-upload-img" />
                    ) : (
                      <ImageIcon className="twin-icon-md twin-floor-upload-placeholder-icon" />
                    )}
                  </div>
                  <div className="twin-floor-upload-actions">
                    <input type="file" ref={fileInputSecondaryRef} onChange={(e) => handleFileUpload(e, setImageSecondary)} accept="image/*" className="twin-hidden" />
                    <button type="button" onClick={() => fileInputSecondaryRef.current?.click()} className="twin-btn twin-btn--floor-amber twin-btn--flex">
                      <Upload className="twin-icon-sm" /> Upload Image B
                    </button>
                    {imageSecondary && (
                      <button type="button" onClick={() => setImageSecondary(undefined)} className="twin-floor-remove-btn" title="Delete image">
                        <X className="twin-icon-sm" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === FOOTER === */}
      <div className="twin-drawer-footer twin-drawer-footer--stacked">
        <div className="twin-footer-hint">
          {isEditing && onDelete && initialMaterial ? (
            <button
              type="button"
              onClick={() => { onDelete(initialMaterial); onClose(); }}
              className="twin-floor-delete-btn"
              title="Delete this floor material from all floors"
            >
              <Trash2 className="twin-icon-sm" />
              <span>Delete Texture</span>
            </button>
          ) : (
            <>
              <Info className="twin-icon-xs twin-floor-accent-icon" />
              <span>Changes will apply to all tiles painted with this material.</span>
            </>
          )}
        </div>

        {/*<div className="twin-footer-divider" />*/}

        <div className="twin-footer-actions">
          <button type="button" onClick={onClose} className="twin-btn-cancel">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim()}
            className="twin-btn-save"
          >
            <Check className="twin-icon-sm" />
            <span>{isEditing ? 'Save Changes' : 'Create Texture'}</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

// === ALIAS DE COMPATIBILIDAD HACIA ATRÁS (se mantiene por convención del proyecto original) ===
export const FloorMaterialModal = FloorMaterialDrawer;